import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbar,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import ReportItemDashboard from "./ItemDashboardReport";
import { LinearProgress, Fade } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useTranslation } from "react-i18next";
function CustomToolbar({ onRefresh }) {
  return (
    <GridToolbarContainer>
      <GridToolbar />
      <Button
        onClick={onRefresh}
        color="primary"
        variant="outlined"
        startIcon={<RefreshIcon />}
        sx={{ ml: 1 }}
      >
        Refresh
      </Button>
    </GridToolbarContainer>
  );
}

function DashboardReport() {
  const { t } = useTranslation(["Report"]);
  const userState = useSelector((state) => state.user);
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [filterExpanded, setFilterExpanded] = useState(false);

  // Date événement
  const [eventFrom, setEventFrom] = useState("");
  const [eventTo, setEventTo] = useState("");

  // Date complétion
  const [completeFrom, setCompleteFrom] = useState("");
  const [completeTo, setCompleteTo] = useState("");
  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/GetCompletedReports/${userState.username}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Erreur API");
      const data = await response.json();
      setRows(data.reports.map((r) => ({ id: r.idActivite, ...r })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [userState]);

  const filteredRows = rows.filter((row) => {
    // Recherche générale
    const searchOk =
      !searchText ||
      [
        row.titleRepportFr,
        row.titleRepportEn,
        row.username,
        row.nomEmplacement,
      ].some((field) =>
        field?.toString().toLowerCase().includes(searchText.toLowerCase()),
      );

    // Recherche emplacement
    const locationOk =
      !locationSearch ||
      row.nomEmplacement?.toLowerCase().includes(locationSearch.toLowerCase());

    // Filtre date événement
    let eventOk = true;

    if (row.dateEvenement) {
      const eventDate = new Date(row.dateEvenement);
      eventDate.setHours(0, 0, 0, 0);

      if (eventFrom) {
        const from = new Date(eventFrom);
        from.setHours(0, 0, 0, 0);

        if (eventDate < from) {
          eventOk = false;
        }
      }

      if (eventTo) {
        const to = new Date(eventTo);
        to.setHours(23, 59, 59, 999);

        if (eventDate > to) {
          eventOk = false;
        }
      }
    }

    // Filtre date complétion
    let completeOk = true;

    if (row.dateComplete) {
      const completeDate = new Date(row.dateComplete);
      completeDate.setHours(0, 0, 0, 0);

      if (completeFrom) {
        const from = new Date(completeFrom);
        from.setHours(0, 0, 0, 0);

        if (completeDate < from) {
          completeOk = false;
        }
      }

      if (completeTo) {
        const to = new Date(completeTo);
        to.setHours(23, 59, 59, 999);

        if (completeDate > to) {
          completeOk = false;
        }
      }
    }

    return searchOk && locationOk && eventOk && completeOk;
  });

  const handleResetFilters = () => {
    setSearchText("");
    setLocationSearch("");

    // Reset date événement
    setEventFrom("");
    setEventTo("");

    // Reset date complétion
    setCompleteFrom("");
    setCompleteTo("");

    // Reset filtre interne DataGrid
    if (apiRef.current) {
      apiRef.current.setFilterModel({
        items: [],
      });
    }
  };

  const handleRefresh = async () => {
    handleResetFilters();
    await fetchReports();
  };

  const formatDateByLang = (value) => {
    if (!value) return "";
    const date = new Date(value);
    const locale = userState.lang === "FR" ? "fr-FR" : "en-US";
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const columns = [
    { field: "idActivite", headerName: "Id", flex: 1 },
    { field: "idTypeActivite", hide: true, filterable: false },
    { field: "titleRepportFr", headerName: "Titre (FR)", flex: 1 },
    { field: "titleRepportEn", headerName: "Titre (EN)", flex: 1 },
    { field: "username", headerName: "Utilisateur", flex: 1 },
    { field: "nomEmplacement", headerName: "Emplacement", flex: 1 },
    {
      field: "dateEvenement",
      headerName: "Date événement",
      type: "dateTime",
      flex: 1,
      valueGetter: (value) => (value ? new Date(value) : null),
      valueFormatter: (value) => formatDateByLang(value),
    },
    {
      field: "dateComplete",
      headerName: "Date complétion",
      type: "dateTime",
      flex: 1,
      valueGetter: (value) => (value ? new Date(value) : null),
      valueFormatter: (value) => formatDateByLang(value),
    },
    {
      field: "details",
      headerName: "Rapport",
      sortable: false,
      filterable: false,
      width: 650, // largeur plus grande pour voir boutons
      cellClassName: "report-action-cell",
      renderCell: (params) => (
        <ReportItemDashboard
          report={params.row}
          typeActivite={
            userState.lang === "EN"
              ? params.row.titleRepportEn
              : params.row.titleRepportFr
          }
          nomEmplacement={params.row.nomEmplacement}
          username={params.row.username}
          date={params.row.dateComplete}
          dateEvent={params.row.dateEvenement}
          setReloadReports={fetchReports} // reload complet après Reopen
        />
      ),
    },
  ];

  return (
    <Box sx={{ height: 700, width: "100%", p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("DisplayedLines")} : {filteredRows.length} / {rows.length}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Accordion
          expanded={filterExpanded}
          onChange={(event, expanded) => setFilterExpanded(expanded)}
          elevation={3}
          sx={{
            mb: 2,
            borderRadius: 3,
            overflow: "hidden",
            "&:before": {
              display: "none",
            },
          }}
        >
          {/* Header filtre */}
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: "white",
              minHeight: 78,
              "& .MuiAccordionSummary-content": {
                margin: "12px 0",
              },
              "& .MuiAccordionSummary-expandIconWrapper": {
                color: "white",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pr: 2,
              }}
            >
              {/* Partie gauche */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.18)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <FilterAltIcon />
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t("filters.title")}
                  </Typography>

                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {t("filters.subtitle")}
                  </Typography>
                </Box>
              </Box>

              {/* Badge */}
              <Box
                sx={{
                  px: 2,
                  py: 0.6,
                  borderRadius: 5,
                  bgcolor: "rgba(255,255,255,0.18)",
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  {
                    [
                      searchText,
                      locationSearch,
                      eventFrom,
                      eventTo,
                      completeFrom,
                      completeTo,
                    ].filter(Boolean).length
                  }{" "}
                  {t("filters.active")}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Divider sx={{ mb: 2 }} />

            {/* Recherche texte */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("filters.search")}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label={t("filters.location")}
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Carte Date événement */}
            <Grid item xs={12}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                    📅 {t("filters.dateFilters")}
                  </Typography>

                  {/* Le contenu sera ajouté à l'étape suivante */}
                  <Box>
                    {/* ================= Event Date ================= */}

                    <Typography
                      variant="subtitle2"
                      color="primary"
                      fontWeight="bold"
                      sx={{ mb: 2 }}
                    >
                      {t("filters.eventDate.title")}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 4 }}>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label={t("filters.eventDate.from")}
                          value={eventFrom ? dayjs(eventFrom) : null}
                          onChange={(newValue) =>
                            setEventFrom(
                              newValue ? newValue.format("YYYY-MM-DD") : "",
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label={t("filters.eventDate.to")}
                          value={eventTo ? dayjs(eventTo) : null}
                          onChange={(newValue) =>
                            setEventTo(
                              newValue ? newValue.format("YYYY-MM-DD") : "",
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ mb: 4 }} />

                    {/* ================= Completion Date ================= */}

                    <Typography
                      variant="subtitle2"
                      color="success.main"
                      fontWeight="bold"
                      sx={{ mb: 2 }}
                    >
                      {t("filters.completeDate.title")}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label={t("filters.completeDate.from")}
                          value={completeFrom ? dayjs(completeFrom) : null}
                          onChange={(newValue) =>
                            setCompleteFrom(
                              newValue ? newValue.format("YYYY-MM-DD") : "",
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label={t("filters.completeDate.to")}
                          value={completeTo ? dayjs(completeTo) : null}
                          onChange={(newValue) =>
                            setCompleteTo(
                              newValue ? newValue.format("YYYY-MM-DD") : "",
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Divider sx={{ my: 3 }} />

            {/* Barre d'actions */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<RestartAltIcon />}
                  onClick={handleResetFilters}
                  disabled={
                    !searchText &&
                    !locationSearch &&
                    !eventFrom &&
                    !eventTo &&
                    !completeFrom &&
                    !completeTo
                  }
                  sx={{
                    minWidth: 180,
                    fontWeight: "bold",
                  }}
                >
                  {t("filters.reset")}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{
                    minWidth: 160,
                    fontWeight: "bold",
                  }}
                >
                  {t("filters.refresh")}
                </Button>
              </Box>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </LocalizationProvider>
      <Fade in={loading} unmountOnExit>
        <Box sx={{ width: "100%", mb: 1 }}>
          <LinearProgress
            color="primary"
            sx={{
              height: 6,
              borderRadius: 3,
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
              },
            }}
          />
        </Box>
      </Fade>

      <DataGridPro
        sx={{
          "& .report-action-cell:focus": {
            outline: "none",
          },
          "& .report-action-cell:focus-within": {
            outline: "none",
          },
        }}
        apiRef={apiRef}
        rows={filteredRows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{ toolbar: { onRefresh: handleRefresh } }}
        pagination={false}
        initialState={{
          filter: {
            filterModel: {
              items: [
                {
                  columnField: "username",
                  operatorValue: "contains",
                  value: "",
                },
              ],
            },
          },
          columns: { columnVisibilityModel: { idTypeActivite: false } },
        }}
      />
    </Box>
  );
}

export default DashboardReport;
